import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate, useSearchParams, useParams, NavigateFunction } from 'react-router-dom';
import { onboardingPath, OnboardingFlowMode } from './onboarding/onboardingRoutes';
import { fetchOnboardingExempt, leaveOnboarding } from '../utils/onboardingExempt';
import { OnboardingBackdrop } from './onboarding/OnboardingLayout';

/**
 * StartEntry page — dual entry point for business and personal onboarding.
 *
 * Routes:
 * - /start/business → sets onboardingMode=business, workspace.type=team,
 *                     purpose=business; skips /onboarding/type, routes to
 *                     /onboarding/setup (R5 — name + optional website)
 * - /start/personal → sets onboardingMode=personal, workspace.type=personal;
 *                     skips /onboarding/type, routes to
 *                     /onboarding/personal-setup (R6 — name your space)
 *
 * Mid-flow same-mode visits resume at the user's current step instead of
 * resetting their progress. A visit in the OTHER mode while the workspace
 * has progressed past its first step asks first — continue, or start over
 * (POST mode with `restart: true`) — and never resets silently (ONB-01).
 * Workspace names that have been customized
 * (anything other than the system defaults "My Personal Space" /
 * "New Workspace" / empty) are preserved verbatim.
 *
 * If the user has no session/workspace, the page creates a workspace
 * first via POST /api/onboarding/create-workspace, then sets the mode.
 * If no session at all, it redirects to /auth/login (preserving the
 * /start/{mode} URL as the continue target so AuthLogin's PR #40
 * routing brings the user back here post-auth).
 *
 * Why we skip /onboarding/type: the type screen used to be the first
 * decision point in onboarding. Once the user lands on /start/{mode},
 * that decision is already made, so the type screen is redundant. The
 * backend PR #38 constraint ensures the mode+type combo we set here is
 * always valid. /onboarding/type is retained as a legacy fallback for
 * workspaces that pre-date this routing.
 */

const SYSTEM_DEFAULT_NAMES = new Set(['', 'My Personal Space', 'New Workspace']);

interface OnboardingStateResponse {
  ok?: boolean;
  code?: string;
  state?: {
    step?: string;
    onboardingMode?: 'business' | 'personal' | null;
    workspaceType?: 'personal' | 'team' | 'enterprise' | null;
    workspaceName?: string;
    selectedOutcomes?: string[];
    reviewAvailable?: boolean;
    isComplete?: boolean;
  };
}

/**
 * Onboarding steps that depend on a committed outcome selection. A workspace
 * recorded at one of these steps with NO selected outcomes is in a corrupt
 * state — resuming there lands the user on a broken pricing/review screen
 * ("Free / 0 outcomes / 0 apps"). See resume validation below.
 */
const STEPS_REQUIRING_OUTCOMES = ['pricing', 'review', 'invites', 'complete'];

/** Each mode's first step (mirrors backend ONBOARDING_FLOWS[mode][0]). */
const FIRST_STEP: Record<OnboardingFlowMode, string> = {
  business: 'setup',
  personal: 'personal-setup',
};

/**
 * Whether a workspace in `mode` at `step` has moved past the mode's first
 * step — i.e. switching it to the other mode would throw work away. Mirrors
 * the backend's `hasOnboardingProgress` (ONB-01).
 */
function hasProgress(mode: OnboardingFlowMode, step: string | undefined): boolean {
  return !!step && step !== 'type' && step !== FIRST_STEP[mode];
}

type ModeState = NonNullable<OnboardingStateResponse['state']>;

type ApplyOutcome =
  | { kind: 'done' }
  | { kind: 'error'; message: string }
  | { kind: 'needs-restart'; from: OnboardingFlowMode };

/**
 * Steps 3–5: set the mode, commit the mode-aligned defaults, route to the
 * mode's first step. `restart` is sent only after the user confirmed starting
 * over (the backend refuses to switch an in-progress workspace without it).
 */
async function applyModeAndStart(args: {
  mode: OnboardingFlowMode;
  state: ModeState;
  restart: boolean;
  continueUrl: string;
  navigate: NavigateFunction;
}): Promise<ApplyOutcome> {
  const { mode, state, restart, continueUrl, navigate } = args;

  // Step 3: Set the mode.
  const modeRes = await fetch('/api/onboarding/mode', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(restart ? { mode, restart: true } : { mode }),
  });

  if (!modeRes.ok) {
    const data = await modeRes.json().catch(() => ({}));
    // The workspace progressed in the other mode (e.g. in another tab since
    // the state read): ask, never reset silently.
    if (data?.code === 'MODE_CHANGE_REQUIRES_RESTART') {
      const from = data?.details?.onboardingMode === 'personal' ? 'personal' : 'business';
      return { kind: 'needs-restart', from };
    }
    return { kind: 'error', message: data.error || 'Failed to set onboarding mode' };
  }

  // Step 4: Commit mode-aligned defaults via PATCH /step type. This
  // both (a) advances onboardingStep past 'type' so we can navigate
  // straight to the next meaningful step, and (b) normalises
  // workspace.type/name when the user transitioned modes (e.g., from
  // personal to business). Only resets workspaceName when current
  // name is blank or a known system default — preserves customized
  // names verbatim.
  const currentName = state.workspaceName ?? '';
  const shouldResetName = SYSTEM_DEFAULT_NAMES.has(currentName);
  const defaultType: 'personal' | 'team' = mode === 'personal' ? 'personal' : 'team';
  const defaultName = mode === 'personal' ? 'My Personal Space' : 'New Workspace';

  const stepPayload: { step: 'type'; data: Record<string, unknown> } = {
    step: 'type',
    data: { type: defaultType },
  };
  if (shouldResetName) {
    stepPayload.data.workspaceName = defaultName;
  }

  const patchRes = await fetch('/api/onboarding/step', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(stepPayload),
  });

  if (!patchRes.ok) {
    const data = await patchRes.json().catch(() => ({}));
    return { kind: 'error', message: data.error || 'Failed to set workspace type' };
  }

  // Step 5: Route directly to the mode-appropriate first step (R7:
  // mode-specific route trees). Business → /onboarding/business/setup,
  // personal → /onboarding/personal/setup. The /onboarding/type
  // screen is skipped entirely — the decision it represented is
  // implicit in the /start/{mode} URL.
  navigate(onboardingPath(mode, FIRST_STEP[mode], continueUrl), { replace: true });
  return { kind: 'done' };
}

/**
 * /start/:mode is inside the sign-up journey, so it wears the same frame as
 * the onboarding steps it hands off to — backdrop and logo, not a bare card
 * on grey. These states are brief, but a brief flash of a different product
 * is exactly what made the journey feel stitched together.
 */
function StartShell({ children }: { children: ReactNode }) {
  return (
    <OnboardingBackdrop>
      <div className="flex flex-1 items-center justify-center p-4">{children}</div>
    </OnboardingBackdrop>
  );
}

export function StartEntryPage() {
  const { mode } = useParams<{ mode: string }>();
  const [error, setError] = useState('');
  // ONB-01: the workspace is mid-onboarding in the OTHER mode. Switching would
  // discard that progress, so the user chooses: continue it, or start over.
  const [switchPrompt, setSwitchPrompt] = useState<{ from: OnboardingFlowMode; state: ModeState } | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    // "Continue my …" re-enters this same route with the other :mode; the
    // component is reused, so a previous prompt must not linger.
    setSwitchPrompt(null);
    if (mode !== 'business' && mode !== 'personal') {
      setError('Invalid entry. Choose business or personal.');
      return;
    }

    async function initEntry() {
      try {
        // Central platform admins (server-decided, DB flag) skip Central
        // onboarding entirely: no workspace, mode or step is created or written.
        if (await fetchOnboardingExempt()) {
          leaveOnboarding(continueUrl, navigate);
          return;
        }

        const suffix = continueUrl ? `?continue=${encodeURIComponent(continueUrl)}` : '';
        // The /start/:mode guard above guarantees a valid mode here.
        const flowMode: OnboardingFlowMode = mode === 'personal' ? 'personal' : 'business';

        // Step 1: Check current onboarding state (and detect session/workspace).
        let stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });

        // No session / no workspace context — try create-workspace, fall back to login.
        if (stateRes.status === 401) {
          const createRes = await fetch('/api/onboarding/create-workspace', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              name: mode === 'personal' ? 'My Personal Space' : 'New Workspace',
            }),
          });

          if (createRes.status === 401) {
            // No session at all — go to login, preserving the /start/{mode}
            // URL so AuthLogin's PR #40 redirect brings us back here.
            // Replace, not push: Back must not return here only to bounce again.
            const returnUrl = `/start/${mode}${suffix}`;
            navigate(`/auth/login?continue=${encodeURIComponent(returnUrl)}`, { replace: true });
            return;
          }

          if (!createRes.ok) {
            const data = await createRes.json().catch(() => ({}));
            // An invited member with no workspace bound: this page-load
            // auto-create is refused for them (ONB-03). Their workspaces are
            // on the picker; creating their own is a deliberate choice made
            // on /onboarding/new.
            if (data?.code === 'ALREADY_IN_WORKSPACE') {
              navigate('/auth/workspace');
              return;
            }
            setError(data.error || 'Failed to create workspace');
            return;
          }

          // Re-fetch state now that the workspace exists.
          stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });
        }

        const stateData = (await stateRes.json()) as OnboardingStateResponse;

        // PR-9: the bound workspace is closed — onboarding cannot resume or
        // continue on it. Route the user to pick an active workspace rather
        // than falling through to set mode / continue the flow.
        if (!stateRes.ok && stateData.code === 'WORKSPACE_CLOSED') {
          navigate('/auth/workspace');
          return;
        }

        const state = stateData.state ?? {};

        // Step 2: Mid-flow same-mode resume.
        // If the user already has this mode AND has advanced past 'type',
        // do NOT POST mode again (which would reset their progress).
        // Just route them to their current step.
        if (
          state.onboardingMode === mode &&
          state.step &&
          state.step !== 'type'
        ) {
          // Resume validation (PR-8): if the recorded step depends on a
          // committed outcome selection but there are no selected outcomes,
          // the workspace is in a corrupt state — resuming would land the
          // user on a broken pricing/review screen. Recover by routing to
          // the outcomes step instead. This never creates a workspace and
          // never routes to /onboarding/new (the create-new chooser).
          const selectedOutcomes = state.selectedOutcomes ?? [];
          if (
            STEPS_REQUIRING_OUTCOMES.includes(state.step) &&
            selectedOutcomes.length === 0
          ) {
            navigate(onboardingPath(flowMode, 'outcomes', continueUrl), { replace: true });
            return;
          }
          // R4: a workspace recorded at the review step with no usable
          // review data must not resume onto an empty review screen.
          // Recover forward to pricing — the conditional progression there
          // skips the empty review step. Never the create-workspace chooser.
          if (state.step === 'review' && state.reviewAvailable === false) {
            navigate(onboardingPath(flowMode, 'pricing', continueUrl), { replace: true });
            return;
          }
          // R5: the business flow no longer has standalone purpose/website
          // steps — they collapsed into `setup`. An in-flight pre-R5
          // workspace recorded at either resumes onto the setup screen.
          // R7: routes are mode-specific (/onboarding/{mode}/{step}).
          const resumeStep =
            state.step === 'purpose' || state.step === 'website' ? 'setup' : state.step;
          navigate(onboardingPath(flowMode, resumeStep, continueUrl), { replace: true });
          return;
        }

        // Step 2b (ONB-01): in progress in the OTHER mode. Switching would
        // reset it to the first step, so it is never done silently — the
        // user picks continue or start over below.
        const appliedMode = state.onboardingMode;
        if (
          (appliedMode === 'business' || appliedMode === 'personal') &&
          appliedMode !== flowMode &&
          hasProgress(appliedMode, state.step)
        ) {
          setSwitchPrompt({ from: appliedMode, state });
          return;
        }

        // Steps 3–5: set the mode, commit defaults, route to the first step.
        const outcome = await applyModeAndStart({ mode: flowMode, state, restart: false, continueUrl, navigate });
        if (outcome.kind === 'needs-restart') {
          setSwitchPrompt({ from: outcome.from, state });
        } else if (outcome.kind === 'error') {
          setError(outcome.message);
        }
      } catch {
        setError('Network error. Please try again.');
      }
    }

    initEntry();
  }, [mode, navigate, continueUrl]);

  if (error) {
    return (
      <StartShell>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 transition-colors"
          >
            Try Again
          </button>
        </div>
      </StartShell>
    );
  }

  if (switchPrompt) {
    const target: OnboardingFlowMode = mode === 'personal' ? 'personal' : 'business';
    const label = (m: OnboardingFlowMode) => (m === 'personal' ? 'personal space' : 'business workspace');
    const suffix = continueUrl ? `?continue=${encodeURIComponent(continueUrl)}` : '';
    const continueExisting = () => navigate(`/start/${switchPrompt.from}${suffix}`, { replace: true });
    const startOver = async () => {
      setIsRestarting(true);
      try {
        const outcome = await applyModeAndStart({
          mode: target,
          state: switchPrompt.state,
          restart: true,
          continueUrl,
          navigate,
        });
        if (outcome.kind === 'error') {
          setSwitchPrompt(null);
          setError(outcome.message);
        }
      } catch {
        setSwitchPrompt(null);
        setError('Network error. Please try again.');
      } finally {
        setIsRestarting(false);
      }
    };
    return (
      <StartShell>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 max-w-md w-full text-center" data-testid="mode-switch-prompt">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            You already started setting up a {label(switchPrompt.from)}
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Continue where you left off, or start over as a {label(target)}. Starting over clears the
            choices you made so far.
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={continueExisting}
              disabled={isRestarting}
              className="px-6 py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 transition-colors disabled:opacity-50"
            >
              Continue my {label(switchPrompt.from)}
            </button>
            <button
              type="button"
              onClick={startOver}
              disabled={isRestarting}
              className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50"
            >
              {isRestarting ? 'Starting over…' : `Start over as a ${label(target)}`}
            </button>
          </div>
        </div>
      </StartShell>
    );
  }

  const isPersonal = mode === 'personal';

  return (
    <StartShell>
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">
          {isPersonal ? 'Setting up your personal space...' : 'Setting up your business workspace...'}
        </p>
      </div>
    </StartShell>
  );
}
